import React, { useState, useEffect } from 'react';
import { orderService } from '../api/orderService';
import { siteService } from '../api/siteService';
import { useNotifications } from '../context/NotificationContext';
import Table from '../components/Common/Table';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import OrderDetailsModal from '../components/Orders/OrderDetailsModal';
import { Eye, Download, Package, TrendingUp, Clock, CheckCircle, RefreshCw, Trash2 } from 'lucide-react';
import '../components/Common/Common.css';

function Orders() {
  const { markAsViewed, newItems } = useNotifications();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [flashingItems, setFlashingItems] = useState([]);
  const [sites, setSites] = useState([]);

  // Map an order's `source` to a human label. Built-in sources plus every
  // vitrine site keyed by its `key`.
  const sourceLabel = (val) => {
    if (val === 'website') return 'Site Web';
    if (val === 'store') return 'Magasin';
    const site = sites.find((s) => s.key === val);
    return site ? site.name : val;
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: searchTerm,
        per_page: 15,
      };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;

      const response = await orderService.getOrders(params);
      setOrders(response.data);
      setLastPage(response.last_page || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const stats = await orderService.getOrderStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, searchTerm, statusFilter, sourceFilter]);

  useEffect(() => {
    siteService.getSites().then(setSites).catch(() => setSites([]));
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, []);

  // Mark all orders as viewed when page loads
  useEffect(() => {
    markAsViewed('orders');
  }, []);

  // Flash new items
  useEffect(() => {
    if (newItems.orders && newItems.orders.length > 0) {
      const newIds = newItems.orders.map(item => item.id);
      setFlashingItems(newIds);

      // Remove flash after 3 seconds
      const timer = setTimeout(() => {
        setFlashingItems([]);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [newItems.orders]);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      try {
        await orderService.deleteOrder(id);
        fetchOrders();
      } catch (error) {
        console.error('Error deleting order:', error);
        alert('Échec de la suppression de la commande');
      }
    }
  };


  const handleViewDetails = (order) => {
    setSelectedOrderId(order.id);
    setShowDetailsModal(true);
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      await orderService.downloadInvoice(orderId);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Échec du téléchargement de la facture');
    }
  };

  const handleStatusChange = () => {
    fetchOrders();
    fetchStatistics();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'badge-warning',
      paid: 'badge-info',
      shipped: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-danger',
    };
    return <span className={`badge ${statusColors[status]}`}>{status}</span>;
  };

  const columns = [
    { key: 'id', label: 'ID', width: '60px' },
    {
      key: 'invoice_number',
      label: 'N° Facture',
      render: (val) => val || 'N/A'
    },
    {
      key: 'client',
      label: 'Client',
      render: (client, row) => (
        <div>
          <div style={{ fontWeight: '500' }}>
            {client ? `${client.first_name} ${client.last_name}` : row.guest_name}
          </div>
          <div style={{ fontSize: '12px', color: '#718096' }}>
            {client ? client.email : row.guest_email}
          </div>
        </div>
      )
    },
    { key: 'total', label: 'Total', render: (val) => `${parseFloat(val).toFixed(2)} DH` },
    { key: 'status', label: 'Statut', render: getStatusBadge },
    {
      key: 'source',
      label: 'Source',
      render: (val) => (
        <span className={`badge ${val === 'website' ? 'badge-info' : val === 'store' ? 'badge-secondary' : 'badge-warning'}`}>
          {sourceLabel(val)}
        </span>
      )
    },
    { key: 'created_at', label: 'Date', render: (val) => new Date(val).toLocaleDateString('fr-FR') },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Gestion des Commandes</h1>
        <button onClick={() => { fetchOrders(); fetchStatistics(); }} className="btn btn-primary">
          <RefreshCw size={18} />
          Actualiser
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '20px',
          marginBottom: '24px'
        }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#5a67d8' }}>
              <Package size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Total Commandes</div>
              <div className="stat-value">{statistics.total_orders || 0}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dd6b20' }}>
              <Clock size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">En attente</div>
              <div className="stat-value">
                {statistics.orders_by_status?.find(s => s.status === 'pending')?.count || 0}
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#38a169' }}>
              <CheckCircle size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Terminées</div>
              <div className="stat-value">
                {statistics.orders_by_status?.find(s => s.status === 'completed')?.count || 0}
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dc143c' }}>
              <TrendingUp size={24} />
            </div>
            <div className="stat-info">
              <div className="stat-label">Revenu Total</div>
              <div className="stat-value">{parseFloat(statistics.total_revenue || 0).toFixed(2)} DH</div>
            </div>
          </div>
        </div>
      )}

      <p style={{ marginBottom: '20px', color: '#718096' }}>
        Gérez les commandes du site web et du magasin. Les commandes terminées apparaissent dans la section Ventes.
      </p>

      <div className="filters">
        <div className="filters-grid">
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Rechercher</label>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher par nom, email, numéro de facture..."
              onClear={() => setCurrentPage(1)}
            />
          </div>
          <div className="form-group">
            <label>Filtrer par statut</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="accepted">Acceptée</option>
              <option value="shipped">Expédié</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
          <div className="form-group">
            <label>Filtrer par source</label>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="">Toutes les sources</option>
              <option value="website">Site Web</option>
              <option value="store">Magasin</option>
              {sites.map((s) => (
                <option key={s.id} value={s.key}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={orders}
        loading={loading}
        actions={false}
        rowClassName={(row) => flashingItems.includes(row.id) ? 'flash-row' : ''}
        customActions={(row) => (
          <div className="action-buttons">
            <button
              onClick={() => handleViewDetails(row)}
              className="icon-btn icon-btn--accent"
              title="Voir les détails"
              aria-label="Voir les détails"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={() => handleDownloadInvoice(row.id)}
              className="icon-btn icon-btn--success"
              title="Télécharger la facture"
              aria-label="Télécharger la facture"
            >
              <Download size={16} />
            </button>
            <button
              onClick={() => handleDelete(row.id)}
              className="icon-btn icon-btn--danger"
              title="Supprimer"
              aria-label="Supprimer"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      {showDetailsModal && selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrderId(null);
          }}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}

export default Orders;
